import React, { useState, useEffect } from 'react';
import { Dialog } from '@mui/material';
import type {
  CustomerAlias,
  CreateCustomerAliasRequest,
  UpdateCustomerAliasRequest,
  Vendor,
} from '@techgitter/contracts';
import { AliasType, ALIAS_TYPE_LABELS } from '@techgitter/contracts';
import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import { SmartSelect, type SmartSelectOption } from '../dropdown';
import { AliasApiError } from '../../services/customerAliasService';

const GLOBAL_VENDOR_CODE = 'GLOBAL';

interface AddEditAliasModalProps {
  open: boolean;
  mode: 'add' | 'edit';
  editAlias: CustomerAlias | null;
  vendors: Vendor[];
  onClose: () => void;
  onSubmitCreate: (payload: CreateCustomerAliasRequest) => Promise<void>;
  onSubmitUpdate: (aliasId: string, payload: UpdateCustomerAliasRequest) => Promise<void>;
}

export const AddEditAliasModal: React.FC<AddEditAliasModalProps> = ({
  open,
  mode,
  editAlias,
  vendors,
  onClose,
  onSubmitCreate,
  onSubmitUpdate,
}) => {
  const [aliasType, setAliasType] = useState<AliasType>(AliasType.VendorMatching);
  const [vendorId, setVendorId] = useState<string>('');
  const [aliasText, setAliasText] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const globalVendor = vendors.find((v) => v.code.toUpperCase() === GLOBAL_VENDOR_CODE);
  const globalVendorMissing = !globalVendor;

  // Non-GLOBAL vendors for VendorMatching dropdown
  const vendorMatchingOptions = vendors.filter(
    (v) => v.code.toUpperCase() !== GLOBAL_VENDOR_CODE && v.isActive
  );

  // Populate form from editAlias when opening in edit mode
  useEffect(() => {
    if (open && mode === 'edit' && editAlias) {
      setAliasType(editAlias.aliasType);
      setVendorId(editAlias.vendorId || '');
      setAliasText(editAlias.alias);
      setIsActive(editAlias.isActive);
      setError(null);
    } else if (open && mode === 'add') {
      setAliasType(AliasType.VendorMatching);
      setVendorId('');
      setAliasText('');
      setIsActive(true);
      setError(null);
    }
  }, [open, mode, editAlias]);

  const handleAliasTypeChange = (newType: AliasType) => {
    setAliasType(newType);
    setError(null);

    if (newType === AliasType.GlobalMatching && globalVendor) {
      setVendorId(globalVendor.vendorId);
    } else if (newType === AliasType.ReferenceOnly) {
      setVendorId('');
    } else if (newType === AliasType.VendorMatching) {
      // Clear if previously set to GLOBAL
      if (globalVendor && vendorId === globalVendor.vendorId) {
        setVendorId('');
      }
    }
  };

  const validate = (): string | null => {
    const trimmed = aliasText.trim();
    if (trimmed.length < 2) return 'Alias must be at least 2 characters.';
    if (aliasType === AliasType.VendorMatching && !vendorId) return 'Vendor is required for Vendor Matching aliases.';
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const trimmedAlias = aliasText.trim();

      if (mode === 'add') {
        const payload: CreateCustomerAliasRequest = {
          alias: trimmedAlias,
          aliasType,
          isActive,
        };
        if (aliasType === AliasType.VendorMatching) {
          payload.vendorId = vendorId;
        }
        // GlobalMatching vendor is resolved server-side
        await onSubmitCreate(payload);
      } else if (editAlias) {
        const payload: UpdateCustomerAliasRequest = {};
        if (trimmedAlias !== editAlias.alias) payload.alias = trimmedAlias;
        if (aliasType !== editAlias.aliasType) payload.aliasType = aliasType;
        if (isActive !== editAlias.isActive) payload.isActive = isActive;

        // Handle vendor changes
        if (aliasType === AliasType.VendorMatching && vendorId !== editAlias.vendorId) {
          payload.vendorId = vendorId;
        }

        if (Object.keys(payload).length === 0) {
          handleClose();
          return;
        }

        await onSubmitUpdate(editAlias.aliasId, payload);
      }

      handleClose();
    } catch (err) {
      if (err instanceof AliasApiError && err.statusCode === 409) {
        setError('This alias is already in use for this vendor. The normalised value must be unique per vendor.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save alias');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setAliasType(AliasType.VendorMatching);
    setVendorId('');
    setAliasText('');
    setIsActive(true);
    setError(null);
    onClose();
  };

  // Alias-type choices — drop Global Matching when the GLOBAL vendor scope isn't
  // configured (a warning below explains why it's unavailable).
  const aliasTypeOptions: SmartSelectOption[] = Object.entries(ALIAS_TYPE_LABELS)
    .filter(([value]) => !(Number(value) === AliasType.GlobalMatching && globalVendorMissing))
    .map(([value, label]) => ({ value, label }));

  const vendorOptions: SmartSelectOption[] = vendorMatchingOptions.map((v) => ({
    value: v.vendorId,
    label: v.name,
  }));

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      aria-label={mode === 'add' ? 'Add alias' : 'Edit alias'}
      PaperProps={{
        sx: {
          background: 'transparent',
          boxShadow: 'none',
          borderRadius: 0,
          overflow: 'visible',
        },
      }}
    >
      <SidePanel
        title={mode === 'add' ? 'Add alias' : 'Edit alias'}
        submitLabel={isSaving ? 'Saving…' : mode === 'add' ? 'Add alias' : 'Save changes'}
        isValid={!isSaving && !!aliasText.trim()}
        onClose={() => {
          if (!isSaving) handleClose();
        }}
        onSubmit={handleSubmit}
      >
        {error && (
          <div className="side-panel__alert side-panel__alert--error">{error}</div>
        )}

        {globalVendorMissing && (
          <div className="side-panel__alert side-panel__alert--warning">
            GLOBAL vendor scope is not configured. Global Matching aliases are unavailable.
          </div>
        )}

        <SmartSelect
          label="Alias type"
          value={String(aliasType)}
          options={aliasTypeOptions}
          onChange={(v) => {
            if (v != null) handleAliasTypeChange(Number(v) as AliasType);
          }}
        />

        {/* Vendor picker — conditional on alias type */}
        {aliasType === AliasType.VendorMatching && (
          <SmartSelect
            label="Vendor"
            placeholder={vendorOptions.length === 0 ? 'No vendors available' : 'Select…'}
            value={vendorId || null}
            options={vendorOptions}
            onChange={(v) => {
              setVendorId(v ?? '');
              if (error) setError(null);
            }}
          />
        )}

        {aliasType === AliasType.GlobalMatching && globalVendor && (
          <FormField label="Vendor" disabled>
            <input value="GLOBAL" readOnly />
          </FormField>
        )}

        {/* ReferenceOnly: no vendor picker */}

        <FormField label="Alias">
          <input
            value={aliasText}
            onChange={(e) => {
              setAliasText(e.target.value);
              if (error) setError(null);
            }}
            placeholder="The customer name as it appears in vendor records"
          />
        </FormField>

        <label className="side-panel__checkbox">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          Active
        </label>
      </SidePanel>
    </Dialog>
  );
};
